import { type MCPServerInfo } from '../../types';
import { extractBareHost, toUrlPathSegment } from '../../../strings';

// Snowflake exposes one MCP server per database/schema, addressed by name:
//   https://<account_url>/api/v2/databases/{database}/schemas/{schema}/mcp-servers/{name}
const MCP_PATH_PREFIX = '/api/v2/databases';

const SnowflakeServer: MCPServerInfo = {
  serverId: 'SNOWFLAKE',
  name: 'Snowflake',
  slug: 'snowflake',
  description: 'Snowflake MCP Server',
  source: {
    sourceType: 'REMOTE_URL',
    transport: 'STREAMABLE_HTTP',
    // The URL is built from four server-level config values: the account host
    // (validated/normalized by extractBareHost) plus the database/schema/name
    // path params that locate a specific MCP server.
    mcpServerUrl: (config: Map<string, string>): string => {
      const host = extractBareHost(config.get('host'), {
        label: 'Snowflake account URL',
        example: 'myorg-myaccount.snowflakecomputing.com'
      });
      const database = toUrlPathSegment(config.get('database'), {
        label: 'Snowflake database'
      });
      const schema = toUrlPathSegment(config.get('schema'), {
        label: 'Snowflake schema'
      });
      const name = toUrlPathSegment(config.get('name'), {
        label: 'Snowflake MCP server name'
      });
      return `https://${host}${MCP_PATH_PREFIX}/${database}/schemas/${schema}/mcp-servers/${name}`;
    }
  },
  authenticationType: 'OAUTH',
  serverConfig: {
    required: ['host', 'database', 'schema', 'name'],
    properties: {
      host: {
        type: 'string',
        description:
          'Snowflake account URL (e.g. myorg-myaccount.snowflakecomputing.com)',
        sensitive: false,
        isUrlParameter: true
      },
      database: {
        type: 'string',
        description: 'Snowflake database name (e.g. my_database)',
        sensitive: false,
        isUrlParameter: true
      },
      schema: {
        type: 'string',
        description: 'Snowflake schema name (e.g. my_schema)',
        sensitive: false,
        isUrlParameter: true
      },
      name: {
        type: 'string',
        description: 'Name of the Snowflake MCP server (e.g. my_mcp_server)',
        sensitive: false,
        isUrlParameter: true
      }
    }
  },
  installConfig: { required: [], properties: {} },
  annotations: {
    hintPreRegistered: true
  }
};

export default SnowflakeServer;
